import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Calendar as CalendarIcon, MapPin, Clock, QrCode } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

function fmt(iso) {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }),
    time: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
    month: d.toLocaleDateString(undefined, { month: "short" }).toUpperCase(),
    day: d.getDate(),
  };
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", guests: 1 });
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data.events || [])).catch(() => {});
  }, []);

  const register = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/event-registrations", { ...form, event_id: active.id, guests: Number(form.guests) });
      setReceipt(r.data);
      toast.success("Registered! Save your QR code for check-in.");
    } catch (err) {
      toast.error("Could not register. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const closeAll = () => {
    setActive(null);
    setReceipt(null);
    setForm({ full_name: "", email: "", guests: 1 });
  };

  return (
    <div data-testid="events-page">
      <section className="section">
        <div className="container-editorial">
          <div className="eyebrow">Events</div>
          <h1 className="mt-4 font-serif text-5xl leading-[1.05] text-ink md:text-6xl">
            Come sit at the <em className="not-italic text-clay">table</em>.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-muted">
            Monthly brunches, retreats, orientations and the annual gala. Register in a click — you'll receive a
            QR code for touch-free check-in at the door.
          </p>

          <div className="mt-14 grid grid-cols-1 gap-5">
            {events.map((e) => {
              const f = fmt(e.date);
              return (
                <article key={e.id} data-testid={`event-${e.id}`} className="grid grid-cols-12 items-center gap-6 rounded-2xl border border-clay/15 bg-white p-6 md:p-8">
                  <div className="col-span-3 md:col-span-2">
                    <div className="rounded-xl bg-oat-100 py-5 text-center">
                      <div className="text-xs font-bold tracking-[0.22em] text-clay">{f.month}</div>
                      <div className="mt-1 font-serif text-4xl text-ink">{f.day}</div>
                    </div>
                  </div>
                  <div className="col-span-9 md:col-span-7">
                    <div className="text-xs font-bold uppercase tracking-[0.22em] text-sage">{e.category}</div>
                    <h3 className="mt-1 font-serif text-2xl text-ink md:text-3xl">{e.title}</h3>
                    <p className="mt-2 text-sm text-ink-muted">{e.description}</p>
                    <div className="mt-3 flex flex-wrap gap-4 text-xs text-ink-muted">
                      <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{f.date} · {f.time}</span>
                      <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{e.location}</span>
                    </div>
                  </div>
                  <div className="col-span-12 flex justify-end md:col-span-3">
                    <button data-testid={`register-${e.id}`} onClick={() => setActive(e)} className="btn-primary">
                      <CalendarIcon className="h-4 w-4" /> Register
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(v) => !v && closeAll()}>
        <DialogContent data-testid="event-register-dialog" className="max-w-xl border-clay/20 bg-oat">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl text-ink">{receipt ? "You're in!" : `Register — ${active?.title}`}</DialogTitle>
            <DialogDescription className="text-ink-muted">
              {receipt ? "Save this QR code — you'll need it at the door." : "Free to attend. A QR check-in code will be issued after registration."}
            </DialogDescription>
          </DialogHeader>

          {receipt ? (
            <div className="rounded-2xl border border-sage/25 bg-white p-8 text-center">
              <div className="mx-auto grid h-40 w-40 place-items-center rounded-2xl border-2 border-dashed border-clay/40 bg-oat-100">
                <QrCode className="h-16 w-16 text-clay" />
              </div>
              <div className="mt-5 font-mono text-lg tracking-widest text-ink" data-testid="event-qr-code">{receipt.qr_code}</div>
              <p className="mt-3 text-sm text-ink-muted">Show this code at check-in.</p>
              <button onClick={closeAll} className="btn-outline mt-8" data-testid="event-close-receipt">Close</button>
            </div>
          ) : (
            <form onSubmit={register} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="field-label">Full name *</label>
                <input required data-testid="event-full-name" className="field-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              </div>
              <div>
                <label className="field-label">Email *</label>
                <input required type="email" data-testid="event-email" className="field-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="field-label">Guests</label>
                <input type="number" min="1" max="10" data-testid="event-guests" className="field-input" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} />
              </div>
              <div className="sm:col-span-2 mt-2 flex justify-end">
                <button type="submit" disabled={loading} data-testid="event-register-submit" className="btn-primary">
                  {loading ? "Registering…" : "Confirm Registration"}
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
